import { useLayoutEffect, useRef, useState } from 'react';

const DEFAULT_ROW_GAP_PX = 12;

/**
 * Decides whether a row's label chips fit beside its title or have to drop to their own line.
 *
 * Attach `rowRef` to the wrapping flex-wrap row, `titleSlotRef` to the (shrinkable) title slot,
 * `titleMeasureRef` to an invisible `whitespace-nowrap` copy of the full title text, and
 * `labelsRef` to the chips container. Render the chips inline while `labelsOnNewLine` is false and
 * inside a `basis-full` child once it is true.
 */
export const useInlineLabelsFit = ({
	hasLabels,
	rowGapPx = DEFAULT_ROW_GAP_PX,
	deps = [],
}: {
	hasLabels: boolean;
	rowGapPx?: number;
	deps?: any[];
}) => {
	const rowRef = useRef<HTMLDivElement | null>(null);
	const titleSlotRef = useRef<HTMLDivElement | null>(null);
	const titleMeasureRef = useRef<HTMLSpanElement | null>(null);
	const labelsRef = useRef<HTMLDivElement | null>(null);
	const [labelsOnNewLine, setLabelsOnNewLine] = useState(false);

	useLayoutEffect(() => {
		if (!hasLabels) {
			setLabelsOnNewLine(false);
			return undefined;
		}
		const containerEl = rowRef.current;
		if (!containerEl) return undefined;
		const check = () => {
			// Read refs fresh each call: labelsRef's node moves between the inline and
			// new-line branches as labelsOnNewLine flips, so a captured reference would go
			// stale after a re-parent and silently measure a detached node.
			const currentContainerEl = rowRef.current;
			const titleSlotEl = titleSlotRef.current;
			const titleEl = titleMeasureRef.current;
			const labelsEl = labelsRef.current;
			if (!currentContainerEl || !titleSlotEl || !titleEl || !labelsEl) return;
			const available = currentContainerEl.clientWidth - titleSlotEl.offsetLeft;
			const titleNaturalWidth = titleEl.scrollWidth;
			const labelsNaturalWidth = labelsEl.scrollWidth;
			setLabelsOnNewLine(titleNaturalWidth + labelsNaturalWidth + rowGapPx > available);
		};
		check();
		const observer = new ResizeObserver(check);
		observer.observe(containerEl);
		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasLabels, rowGapPx, ...deps]);

	return { rowRef, titleSlotRef, titleMeasureRef, labelsRef, labelsOnNewLine };
};

export default useInlineLabelsFit;
